import { supabase } from '@/integrations/supabase/client';
import { TerraceFormValues } from '@/components/terrace-submission/types';

export interface SubmissionData {
  extractedCoordinates?: { lat: number; lng: number } | null;
  selectedPhotoUrl?: string | null;
  extractedPlaceId?: string | null;
  extractedPlaceName?: string | null;
  originalMapsUrl?: string | null;
}

export class SubmissionService {
  static async submitTerrace(
    formData: TerraceFormValues,
    submissionData: SubmissionData,
    userId?: string
  ) {
    const { extractedCoordinates, extractedPlaceId, extractedPlaceName, originalMapsUrl } = submissionData;

    // Use extracted coordinates if available, otherwise fall back to default Stockholm coordinates
    const lat = extractedCoordinates?.lat || 59.3293;
    const lng = extractedCoordinates?.lng || 18.0686;

    // Convert detailed opening hours to legacy format for opening_hours field
    const formattedHours = this.formatOpeningHours(formData.detailedOpeningHours);
    const openDays = this.extractOpenDays(formData.detailedOpeningHours);

    // Determine submission type - always use 'guest' for non-authenticated users
    const submittedBy = userId ? userId : 'guest';
    
    const submission = {
      name: formData.name,
      type: formData.type,
      address: formData.address,
      openinghours: formattedHours || formData.openingHours || "",
      detailed_opening_hours: formData.detailedOpeningHours,
      neighborhood: openDays.join(", "), // Store summary in neighborhood for now
      terracedescription: formData.terraceDescription,
      website: formData.website || "",
      phone: formData.phone || "",
      hasfood: formData.hasFood,
      servesalcohol: formData.servesAlcohol,
      petfriendly: formData.petFriendly,
      wheelchair_accessible: formData.wheelchairAccessible,
      serves_wine: formData.servesWine,
      serves_beer: formData.servesBeer,
      allows_dogs: formData.allowsDogs,
      lat: lat,
      lng: lng,
      status: "pending",
      submitted_by: submittedBy,
      submitter_name: formData.submitterName || "",
      submitted_at: new Date().toISOString(),
      sunstatus: formData.sunStatus,
      orientation: formData.orientation || "unknown",
      google_place_id: extractedPlaceId || null,
      place_name: extractedPlaceName || null,
      photo_url: formData.photo_url || null,
      photos_processing: false,
      photos_processed: false,
      photo_count: 0,
      original_maps_url: originalMapsUrl || null
    };

    const { data: submissionResult, error } = await supabase
      .from("terrace_submissions")
      .insert(submission)
      .select('id')
      .single();

    if (error) {
      console.error("Submission error:", error);
      throw new Error("Failed to submit terrace. Please try again.");
    }

    return submissionResult;
  }

  static async fetchPhotosForSubmission(submissionId: string, placeId: string, placeName: string) {
    try {
      
      const response = await fetch('/api/functions/v1/fetch-place-photos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          placeId: placeId,
          placeName: placeName,
          submissionId: submissionId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch photos');
      }

      const data = await response.json();
      return data.photos || [];
    } catch (error) {
      console.error("Error fetching photos:", error);
      return [];
    }
  }

  static async cachePhoto(photoReference: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.functions.invoke('cache-place-photo', {
        body: { photoReference }
      });

      if (error) throw error;
      return data?.cachedUrl || null;
    } catch (error) {
      console.error('Error caching photo:', error);
      return null;
    }
  }

  static async updateSubmissionPhoto(submissionId: string, photoUrl: string) {
    const { error } = await supabase
      .from("terrace_submissions")
      .update({ 
        photo_url: photoUrl,
        photos_processed: true,
        photo_count: 1
      })
      .eq('id', submissionId);

    if (error) {
      console.error("Error updating submission photo:", error);
      throw error;
    }
  }

  private static formatOpeningHours(detailedHours: any): string {
    let formattedHours = "";
    
    Object.entries(detailedHours).forEach(([day, hours]: [string, any]) => {
      if (hours.isOpen) {
        if (formattedHours) formattedHours += ", ";
        formattedHours += `${day.charAt(0).toUpperCase() + day.slice(1)}: ${hours.from} - ${hours.to}`;
      }
    });

    return formattedHours;
  }

  private static extractOpenDays(detailedHours: any): string[] {
    const openDays: string[] = [];
    
    Object.entries(detailedHours).forEach(([day, hours]: [string, any]) => {
      if (hours.isOpen) {
        openDays.push(day);
      }
    });

    return openDays;
  }
}
